import { Input } from "@/components/UI";
import MedicalReportPromptType from "@/data/interfaces/MedicalReportPromptType";
import medicalReportProtocolService, {
  UploadResponse,
} from "@/services/medicalReportProtocolService";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { CheckIcon, XIcon } from "lucide-react";

interface MedicalReportUploadCardProps {
  type: MedicalReportPromptType;
  onUploadSuccess?: (response: UploadResponse) => void;
}

const MedicalReportUploadCard = ({
  type,
  onUploadSuccess,
}: MedicalReportUploadCardProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [response, setResponse] = useState<UploadResponse>();
  const [errorMsg, setErrorMsg] = useState<string>();
  const [inputKey, setInputKey] = useState(0);

  const handleFileChange = async (file: File | null) => {
    setErrorMsg(undefined);
    setResponse(undefined);

    if (!file) {
      return;
    }

    try {
      setIsUploading(true);

      const data = await medicalReportProtocolService.upload({
        type_id: type.id,
        file: file,
      });

      setResponse(data);
      onUploadSuccess?.(data);
    } catch (error) {
      setInputKey((prev) => prev + 1);
      console.error(error);
      setErrorMsg(`UPLOAD FAILED: ${error}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-md bg-neutral-200 px-2 py-2",
        isUploading && "bg-blue-100",
        errorMsg && "bg-red-100",
        response && "bg-green-100",
      )}
    >
      <Input
        key={inputKey}
        type="file"
        required
        accept=".pdf"
        label={type.name}
        labelClassName="text-lg font-medium line-clamp-1"
        onChange={(e) => {
          const file = e.target.files?.[0] || null;
          handleFileChange(file);
        }}
      />

      <p className="flex items-center gap-1">
        {isUploading && "Getting answer..."}
        {errorMsg && (
          <>
            <XIcon className="h-4 w-4" />
            <span className="line-clamp-1">{errorMsg}</span>
          </>
        )}
        {response && (
          <>
            <CheckIcon className="h-4 w-4" />
            <span>Success</span>
          </>
        )}
      </p>
    </div>
  );
};

export default MedicalReportUploadCard;
